import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { signIn } from "@/lib/auth-client";
import { DEV_ADMIN_PASSWORD, DEV_USER_PASSWORD } from "@/lib/dev-accounts";
import { devKeys } from "@/lib/query";

const loginSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
});

type LoginValues = z.infer<typeof loginSchema>;

const isDev = process.env.NODE_ENV !== "production";

const seedAccountsSchema = z.object({
  admin: z.object({ email: z.string().email() }),
  user: z.object({ email: z.string().email() }),
});

type DevAccount = { label: string; email: string; password: string };

function useDevAccounts() {
  return useQuery({
    queryKey: devKeys.seedAccounts,
    enabled: isDev,
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: async (): Promise<DevAccount[]> => {
      const res = await fetch("/api/dev/seed-accounts");
      if (!res.ok) return [];
      const parsed = seedAccountsSchema.parse(await res.json());
      return [
        { label: "Admin", email: parsed.admin.email, password: DEV_ADMIN_PASSWORD },
        { label: "User", email: parsed.user.email, password: DEV_USER_PASSWORD },
      ];
    },
  });
}

export function LoginForm() {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });
  const devAccounts = useDevAccounts().data ?? [];

  function fillCredentials(email: string, password: string) {
    setValue("email", email, { shouldValidate: true });
    setValue("password", password, { shouldValidate: true });
  }

  async function onSubmit(values: LoginValues) {
    const { error } = await signIn.email({ email: values.email, password: values.password });
    if (error) {
      toast.error(error.message ?? "Failed to sign in");
      return;
    }
    window.location.href = "/dashboard";
  }

  return (
    <div style={cardStyle}>
      <div style={{ textAlign: "center", marginBottom: 28 }}>
        <h1 style={headingStyle}>Welcome back</h1>
        <p style={{ fontSize: 13, color: "#608099" }}>Sign in to continue your training</p>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{ display: "flex", flexDirection: "column", gap: 16 }}
      >
        <div>
          <label htmlFor="login-email" style={labelStyle}>
            Email
          </label>
          <input
            id="login-email"
            type="email"
            placeholder="you@nymbl.app"
            style={inputStyle}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = "#4334dc";
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = "#e0e0e0";
            }}
            {...register("email")}
          />
          {errors.email && <p style={errorStyle}>{errors.email.message}</p>}
        </div>

        <div>
          <label htmlFor="login-password" style={labelStyle}>
            Password
          </label>
          <input
            id="login-password"
            type="password"
            placeholder="••••••••"
            style={inputStyle}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = "#4334dc";
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = "#e0e0e0";
            }}
            {...register("password")}
          />
          {errors.password && <p style={errorStyle}>{errors.password.message}</p>}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          style={isSubmitting ? { ...btnStyle, opacity: 0.6, cursor: "not-allowed" } : btnStyle}
        >
          {isSubmitting ? "Signing in…" : "Sign In"}
        </button>
      </form>

      {isDev && devAccounts.length > 0 && (
        <div
          style={{
            marginTop: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          <span style={{ fontSize: 11, color: "#608099" }}>Sign in as:</span>
          {devAccounts.map((account) => (
            <button
              key={account.label}
              type="button"
              onClick={() => fillCredentials(account.email, account.password)}
              style={devBtnStyle}
            >
              {account.label}
            </button>
          ))}
        </div>
      )}

      <p style={{ textAlign: "center", fontSize: 13, color: "#608099", marginTop: 24 }}>
        Don't have an account?{" "}
        <a href="/signup" style={{ color: "#4334dc", textDecoration: "none", fontWeight: 600 }}>
          Sign up
        </a>
      </p>
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  background: "#ffffff",
  border: "1px solid #eeeeee",
  borderRadius: 12,
  padding: "36px 32px",
  boxShadow: "0 10px 40px rgba(0,0,0,0.06)",
};

const headingStyle: React.CSSProperties = {
  fontFamily: "'Raleway', sans-serif",
  fontSize: 26,
  fontWeight: 800,
  color: "#0f1944",
  letterSpacing: "-0.02em",
  marginBottom: 6,
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "#608099",
  marginBottom: 6,
  fontFamily: "'Montserrat', sans-serif",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "11px 14px",
  border: "1.5px solid #e0e0e0",
  borderRadius: 8,
  fontFamily: "'Montserrat', sans-serif",
  fontSize: 14,
  color: "#0f1944",
  background: "#ffffff",
  outline: "none",
  transition: "border-color 0.2s",
  boxSizing: "border-box",
};

const btnStyle: React.CSSProperties = {
  width: "100%",
  padding: "13px",
  background: "#4334dc",
  color: "#ffffff",
  border: "none",
  borderRadius: 8,
  fontFamily: "'Montserrat', sans-serif",
  fontSize: 14,
  fontWeight: 700,
  cursor: "pointer",
  letterSpacing: "0.02em",
  marginTop: 4,
  transition: "background 0.2s",
};

const devBtnStyle: React.CSSProperties = {
  padding: "4px 12px",
  fontSize: 11,
  fontWeight: 600,
  fontFamily: "'Montserrat', sans-serif",
  background: "rgba(67,52,220,0.08)",
  color: "#4334dc",
  border: "1px solid rgba(67,52,220,0.2)",
  borderRadius: 50,
  cursor: "pointer",
};

const errorStyle: React.CSSProperties = {
  fontSize: 12,
  color: "#dc2626",
  marginTop: 4,
  fontFamily: "'Montserrat', sans-serif",
};
